import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tag,
  Space,
  Spin,
  Grid,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { fetchCategories } from '../../store/slices/categoriesSlice';
import categoryService from '../../services/categoryService';
import ErrorMessage from '../common/ErrorMessage';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;
const { Option } = Select;

const CategoryManager = () => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { categories, loading, error } = useSelector((state) => state.categories);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchCategories(true));
  }, [dispatch]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      form.setFieldsValue({
        name: category.name || '',
        description: category.description || '',
        parent_id: category.parent_id || null,
      });
    } else {
      setEditingCategory(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCategory) {
        await categoryService.update(editingCategory.id, values);
      } else {
        await categoryService.create(values);
      }

      handleCloseDialog();
      dispatch(fetchCategories(true));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save category:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Haqiqatan ham ushbu kategoriyani o\'chirmoqchimisiz?')) {
      try {
        await categoryService.delete(id);
        dispatch(fetchCategories(true));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete category:', err);
      }
    }
  };

  if (loading && !categories.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Yuklanmoqda..." />
      </div>
    );
  }

  const parentCategories = categories.filter((cat) => !cat.parent_id);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Nomi',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Tavsif',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || 'Mavjud emas',
      responsive: ['md'],
    },
    {
      title: 'Asosiy kategoriya',
      key: 'parent',
      render: (_, record) => (
        record.ParentCategory
          ? record.ParentCategory.name
          : record.parent_id
            ? 'N/A'
            : 'Asosiy'
      ),
      responsive: ['sm'],
    },
    {
      title: 'Holati',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.is_active ? 'success' : 'default'}>
          {record.is_active ? 'Faol' : 'Faol emas'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenDialog(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
      align: 'right',
      width: 100,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'stretch' : 'center',
          gap: 16,
          marginBottom: 24
        }}
      >
        <Title level={2} style={{ margin: 0 }}>Kategoriyalarni boshqarish</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenDialog()}
          block={screens.xs}
        >
          Kategoriya qo&apos;shish
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <Table
        columns={columns}
        dataSource={categories}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 800 }}
      />

      {/* Add/Edit Dialog */}
      <Modal
        title={editingCategory ? 'Kategoriyani tahrirlash' : 'Yangi kategoriya qo\'shish'}
        open={openDialog}
        onCancel={handleCloseDialog}
        onOk={() => form.submit()}
        okText={editingCategory ? 'Yangilash' : 'Yaratish'}
        cancelText="Bekor qilish"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Kategoriya nomi"
            rules={[{ required: true, message: 'Iltimos kategoriya nomini kiriting' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Tavsif"
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="parent_id"
            label="Asosiy kategoriya"
            extra="Asosiy kategoriya bo'lsa bo'sh qoldiring"
          >
            <Select placeholder="Asosiy kategoriyani tanlang" allowClear>
              {parentCategories.map((category) => (
                <Option
                  key={category.id}
                  value={category.id}
                  disabled={editingCategory && category.id === editingCategory.id}
                >
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManager;
